import React, { useEffect, useState } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  TextField,
  Typography,
  Button,
  Divider,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import LinkIcon from '@mui/icons-material/Link';
import { Icon } from '@iconify/react/dist/iconify.js';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import {
  editJob,
  getAllGoals,
  getGoalStatusesForFilterTabs,
  getSourceTasksForGoal,
} from '../../../redux/features/appraisalSlice';
import { useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';

// department_task_assignments.assignment_status (task_management.controller.js)
const TASK_ASSIGNMENT_STATUS_COMPLETED = 5;

const JobsSelectionArea = ({ goal, jobActivityDisabled, dispatchData, adminView }) => {
  const [editMode, setEditMode] = useState(false);
  const [initialJobs, setInitialJobs] = useState([]);
  const [editedJobs, setEditedJobs] = useState([]);
  const [changeDetected, setChangeDetected] = useState(false);
  const [linkedTasksByJob, setLinkedTasksByJob] = useState({});
  // The job currently being removed, while we wait on the "cancel the linked
  // task too?" confirmation. Null when no prompt is open.
  const [removeJobPromptFor, setRemoveJobPromptFor] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    setInitialJobs(goal.jobs);
    setEditedJobs(goal.jobs.map((job) => ({ ...job }))); //SHALLOW COPYING BASICALLY DOING THE SAMETHING BOTH INITIAL AND EDITED CONTAIN SAME ARRAY
  }, [goal]);

  useEffect(() => {
    if (!goal?.goal_id) return;
    dispatch(getSourceTasksForGoal(goal.goal_id)).then((res) => {
      if (res.payload?.status === 'success' && Array.isArray(res.payload.data)) {
        const byJob = {};
        res.payload.data.forEach((link) => {
          byJob[link.goal_job_id] = link;
        });
        setLinkedTasksByJob(byJob);
      }
    });
  }, [goal?.goal_id, dispatch]);

  const goToLinkedTask = () => {
    window.open('/task-management/my-tasks', '_blank', 'noopener,noreferrer');
  };

  const handleJobFieldEdit = (id, field, value) => {
    const updated = editedJobs.map((job) =>
      job.goal_job_id === id ? { ...job, [field]: value === false ? 0 : value === true ? 1 : value } : job,
    );
    setEditedJobs(updated);
    checkForChanges(updated);
  };

  // "Remove Job" is only worth asking about if the job has a linked task
  // that isn't already completed - deleting a job shouldn't retroactively
  // cancel finished work, so those go straight through with no prompt.
  const handleRemoveJobClick = (job) => {
    const link = linkedTasksByJob[job.goal_job_id];
    if (link && link.assignment_status !== TASK_ASSIGNMENT_STATUS_COMPLETED) {
      setRemoveJobPromptFor(job);
      return;
    }
    handleJobFieldEdit(job.goal_job_id, 'goal_job_striked', true);
  };

  const confirmRemoveJob = (cancelLinkedTask) => {
    const jobId = removeJobPromptFor?.goal_job_id;
    setRemoveJobPromptFor(null);
    const updated = editedJobs.map((job) =>
      job.goal_job_id === jobId ? { ...job, goal_job_striked: 1, cancel_linked_task: cancelLinkedTask } : job,
    );
    setEditedJobs(updated);
    checkForChanges(updated);
  };

  const checkForChanges = (updatedJobs) => {
    const hasChanges = updatedJobs.some(
      (job, idx) =>
        job.goal_job_name !== initialJobs[idx].goal_job_name ||
        job.goal_job_start_date !== initialJobs[idx].goal_job_start_date ||
        job.goal_job_end_date !== initialJobs[idx].goal_job_end_date ||
        job.goal_job_completed !== initialJobs[idx].goal_job_completed ||
        job.goal_job_striked !== initialJobs[idx].goal_job_striked,
    );
    setChangeDetected(hasChanges);
  };

  const handleEditAll = () => {
    setEditMode(true);
  };

  const handleCancel = () => {
    const resetJobs = initialJobs.map((job) => ({ ...job }));
    setEditedJobs(resetJobs);
    setEditMode(false);
    setChangeDetected(false);
  };

  const handleUpdate = () => {
    const data = { data: editedJobs, id: goal?.last_extend_id };
    dispatch(editJob(data)).then((res) => {
      if (res.payload.status === 'success') {
        enqueueSnackbar(res.payload.message, {
          variant: 'success',
        });
        handleCancel();
        dispatch(getAllGoals(dispatchData));
        dispatch(getGoalStatusesForFilterTabs(dispatchData));
      } else {
        enqueueSnackbar(res.payload.message, {
          variant: 'error',
        });
        const resetJobs = initialJobs.map((job) => ({ ...job }));
        setEditedJobs(resetJobs);
      }
    });
  };

  const addNewJobNavigateFunction = () => {
    const passingObject = {
      goal: goal,
      newJobFieldRequired: true,
      adminView: adminView,
    };
    navigate('/Appraisal-Management/add-goal', { state: passingObject });
  };

  const allJobsAlreadyMarked = editedJobs
    .filter((job) => !job.goal_job_striked)
    .every((job) => job.goal_job_completed === 1);

  const toggleMarkAllJobs = () => {
    // Check if ALL unstriked jobs are already marked
    const allMarked = editedJobs.filter((job) => !job.goal_job_striked).every((job) => job.goal_job_completed === 1);
    console.log('allmarked', allMarked);

    const updatedJobs = editedJobs.map((job) =>
      job.goal_job_striked == 1
        ? job // Skip striked jobs
        : { ...job, goal_job_completed: allMarked ? 0 : 1 },
    );
    setEditedJobs(updatedJobs);
    checkForChanges(updatedJobs);
  };
  return (
    <Box display="flex" flexDirection="column" gap={0}>
      <Box
        px={1}
        pt={1}
        pb={1}
        display="flex"
        justifyContent="flex-start"
        gap={1}
        alignItems="center"
        sx={{
          position: 'sticky',
          top: -8,
          zIndex: 2,
          backgroundColor: '#fff',
          flexWrap: 'wrap',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Button
          disabled={jobActivityDisabled}
          sx={{
            minWidth: '130px',
            fontSize: '0.65rem',
            borderRadius: '4px',
            boxShadow: 'none',
            '&:hover': {
              bgcolor: 'primary.main',
              color: 'white',
            },
          }}
          size="small"
          color="primary"
          variant="outlined"
          onClick={addNewJobNavigateFunction}
          startIcon={<Icon icon="gridicons:add" />}
        >
          Add New Job
        </Button>

        {/* <Button
          sx={{
            minWidth: "130px",
            fontSize: "0.65rem",
            borderRadius: "4px",
            boxShadow: "none",
            "&:hover": {
              bgcolor: "info.main",
              color: "white",
            },
          }}
          size="small"
          color="info"
          variant="outlined"
          onClick={toggleMarkAllJobs}
          startIcon={
            allJobsAlreadyMarked ? (
              <Icon icon="mdi:checkbox-blank-outline" />
            ) : (
              <Icon icon="mdi:checkbox-marked" />
            )
          }
        >
          {allJobsAlreadyMarked ? "Unmark All Jobs" : "Mark All Jobs"}
        </Button> */}

        {!editMode && (
          <Button
            disabled={jobActivityDisabled}
            sx={{
              minWidth: '130px',
              fontSize: '0.65rem',
              borderRadius: '4px',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: 'secondary.main',
                color: 'white',
              },
            }}
            size="small"
            color="secondary"
            variant="outlined"
            onClick={handleEditAll}
            startIcon={<Icon icon="raphael:edit" />}
          >
            Edit Job Details
          </Button>
        )}

        <Box>
          {changeDetected && (
            <Box display="flex" justifyContent="flex-end" gap={0}>
              <Button
                disabled={jobActivityDisabled}
                sx={{
                  minWidth: '130px',
                  fontSize: '0.65rem',
                  borderRadius: '4px',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: 'warning.main',
                    color: 'white',
                  },
                }}
                size="small"
                color="warning"
                variant="outlined"
                onClick={handleUpdate}
                startIcon={<Icon icon="radix-icons:update" />}
              >
                Update Changes
              </Button>
            </Box>
          )}
        </Box>
        {(changeDetected || editMode) && (
          <Button
            disabled={jobActivityDisabled}
            sx={{
              minWidth: '130px',
              fontSize: '0.65rem',
              borderRadius: '4px',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: 'success.main',
                color: 'white',
              },
            }}
            size="small"
            color="success"
            variant="outlined"
            onClick={handleCancel}
            startIcon={<Icon icon="grommet-icons:revert" />}
          >
            Cancel Changes
          </Button>
        )}
      </Box>

      {/* CHECK BOX CONTENT AREA */}

      {editedJobs.map((job, index) => (
        <Box key={`${job?.goal_job_id}-${index}}`} display="flex" flexDirection="column" gap={0.5} mt={1}>
          {index !== 0 && (
            <Divider
              textAlign="center"
              sx={{
                fontSize: '0.8rem',
                fontWeight: 600,
                color: 'primary.main',
                my: 0.5,
                '&::before, &::after': {
                  borderColor: '#ccc',
                },
              }}
            ></Divider>
          )}
          <Box display="flex" alignItems="center">
            <Checkbox
              disabled={
                job.goal_job_striked || jobActivityDisabled ? true : false || goal?.appraisal_status_name != 'Approved'
              }
              size="small"
              checked={job.goal_job_completed ? true : false}
              onChange={(e) => handleJobFieldEdit(job.goal_job_id, 'goal_job_completed', e.target.checked)}
            />

            {editMode ? (
              <TextField
                disabled={job.goal_job_striked ? true : false}
                fullWidth
                size="small"
                value={job.goal_job_name}
                onChange={(e) => handleJobFieldEdit(job.goal_job_id, 'goal_job_name', e.target.value)}
                inputProps={{ style: { fontSize: '0.9rem' } }}
              />
            ) : (
              <Box>
                <Typography
                  sx={{
                    textDecoration: job.goal_job_striked ? 'line-through' : 'none',
                    flex: 1,
                    textTransform: 'capitalize',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    textAlign: 'justify',
                    fontSize: {
                      xs: '0.85rem',
                      sm: '0.9rem',
                    },
                    alignSelf: 'center',
                  }}
                >
                  {job.goal_job_name}
                </Typography>
              </Box>
            )}
          </Box>

          {editMode ? (
            <Box sx={{ display: 'flex', alignItems: 'center', pl: 4.8, gap: 1 }}>
              <TextField
                disabled={job.goal_job_striked ? true : false}
                inputProps={{ style: { fontSize: '0.75rem' } }}
                size="small"
                type="date"
                value={job.goal_job_start_date}
                onChange={(e) => handleJobFieldEdit(job.goal_job_id, 'goal_job_start_date', e.target.value)}
              />
              <TextField
                disabled={job.goal_job_striked ? true : false}
                inputProps={{ style: { fontSize: '0.75rem' } }}
                size="small"
                type="date"
                value={job.goal_job_end_date}
                onChange={(e) => handleJobFieldEdit(job.goal_job_id, 'goal_job_end_date', e.target.value)}
              />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', pl: 5, gap: 2, flexWrap: 'wrap' }}>
              <Typography
                label="Start Date"
                variant="caption"
                sx={{
                  color: '#888',
                  fontSize: {
                    xs: '0.68rem',
                    sm: '0.75rem',
                  },
                  textDecoration: job.goal_job_striked ? 'line-through' : 'none',
                }}
              >
                Start Date: {dayjs(job.goal_job_start_date).format('DD-MM-YYYY')}
              </Typography>
              <Typography
                label="End Date"
                variant="caption"
                sx={{
                  color: '#888',
                  fontSize: {
                    xs: '0.68rem',
                    sm: '0.75rem',
                  },
                  textDecoration: job.goal_job_striked ? 'line-through' : 'none',
                }}
              >
                End Date : {dayjs(job.goal_job_end_date).format('DD-MM-YYYY')}
              </Typography>

              {linkedTasksByJob[job.goal_job_id] && (
                <Tooltip
                  title={`Linked Task Management task: "${linkedTasksByJob[job.goal_job_id].title}" - ${
                    linkedTasksByJob[job.goal_job_id].assignment_status === TASK_ASSIGNMENT_STATUS_COMPLETED
                      ? 'completed'
                      : 'in progress'
                  }. Click to open Task Management in a new tab.`}
                >
                  <Chip
                    size="small"
                    icon={<LinkIcon sx={{ fontSize: '0.9rem !important' }} />}
                    label="Task"
                    color={
                      linkedTasksByJob[job.goal_job_id].assignment_status === TASK_ASSIGNMENT_STATUS_COMPLETED
                        ? 'success'
                        : 'info'
                    }
                    variant="outlined"
                    onClick={goToLinkedTask}
                    sx={{ fontSize: '0.65rem' }}
                  />
                </Tooltip>
              )}
            </Box>
          )}

          {editMode && (
            <Box display="flex" sx={{ pl: 4.8 }}>
              {job.goal_job_striked ? (
                <Button
                  sx={{
                    display: adminView ? 'inline-flex' : 'none',
                    minWidth: '130px',
                    fontSize: '0.65rem',
                    borderRadius: '4px',
                    boxShadow: 'none',
                    '&:hover': {
                      bgcolor: 'success.main',
                      color: 'white',
                    },
                  }}
                  size="small"
                  color="success"
                  variant="outlined"
                  onClick={(e) => handleJobFieldEdit(job.goal_job_id, 'goal_job_striked', false)}
                  startIcon={<Icon icon="la:trash-restore-alt" width="21" height="20" />}
                >
                  Recover Job
                </Button>
              ) : (
                <Button
                  sx={{
                    minWidth: '130px',
                    fontSize: '0.65rem',
                    borderRadius: '4px',
                    boxShadow: 'none',
                    '&:hover': {
                      bgcolor: 'error.main',
                      color: 'white',
                    },
                  }}
                  size="small"
                  color="error"
                  variant="outlined"
                  onClick={() => handleRemoveJobClick(job)}
                  startIcon={<Icon icon="mi:delete" />}
                >
                  Remove Job
                </Button>
              )}
            </Box>
          )}
        </Box>
      ))}

      {/* This job has a linked, not-yet-completed task - ask whether to
          cancel that task too, instead of silently leaving it orphaned. */}
      <Dialog open={!!removeJobPromptFor} onClose={() => setRemoveJobPromptFor(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Cancel the linked task too?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            This job has a linked task in Task Management. Removing the job won't cancel it automatically.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button size="small" color="inherit" onClick={() => setRemoveJobPromptFor(null)}>
            Cancel
          </Button>
          <Button size="small" onClick={() => confirmRemoveJob(false)}>
            No, just remove the job
          </Button>
          <Button size="small" variant="contained" color="error" onClick={() => confirmRemoveJob(true)}>
            Yes, cancel the task too
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default JobsSelectionArea;
